import { useTranslation } from 'react-i18next';

import type { HealthParameterKey } from '@/domain/health';
import { getHealthMetricUiConfig } from '@/routes/Client/Cage/pages/Health/HealthDashboard/healthMetricConfig';

import styles from './HealthLaksvelAlertsTooltipContent.module.scss';

export interface HealthLaksvelAlertItem {
    parameterKey: HealthParameterKey;
    level: 'high' | 'increasing';
}

interface HealthLaksvelAlertsTooltipContentProps {
    alerts: HealthLaksvelAlertItem[];
}

/** Tooltip body shared by location overview Laksvel cell and cage health card. */
export default function HealthLaksvelAlertsTooltipContent(
    props: HealthLaksvelAlertsTooltipContentProps
) {
    const { alerts } = props;
    const { t: tHealth } = useTranslation('healthModule');
    const { t: tGlobal } = useTranslation();

    if (alerts.length === 0) {
        return (
            <div className={styles.healthTooltipBody}>
                <p className={styles.statusBody}>{tHealth('locationOverview.statusTooltipBody')}</p>
            </div>
        );
    }

    const highAlerts = alerts.filter((alert) => alert.level === 'high');
    const increasingAlerts = alerts.filter((alert) => alert.level === 'increasing');

    return (
        <div className={styles.healthTooltipBody}>
            <AlertGroup
                title={tHealth('locationOverview.groupSignificantlyElevated')}
                alerts={highAlerts}
                tGlobal={tGlobal}
            />
            <AlertGroup
                title={tHealth('locationOverview.groupRapidlyIncreasing')}
                alerts={increasingAlerts}
                tGlobal={tGlobal}
            />
        </div>
    );
}

interface AlertGroupProps {
    title: string;
    alerts: HealthLaksvelAlertItem[];
    tGlobal: (key: string) => string;
}

function AlertGroup(props: AlertGroupProps) {
    const { title, alerts, tGlobal } = props;

    if (alerts.length === 0) {
        return null;
    }

    return (
        <div className={styles.alertGroup}>
            <p className={styles.alertGroupTitle}>{title}</p>
            <ul className={styles.alertList}>
                {alerts.map((alert) => (
                    <li key={alert.parameterKey} className={styles.alertRow}>
                        <span className={styles.alertParameter}>
                            {tGlobal(getHealthMetricUiConfig(alert.parameterKey).labelKey)}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}
